// KPI 모니터링 관련 타입 정의

export const TRANSMIT_CYCLES = {
  DAILY: 'D',
  WEEKLY: 'W', 
  MONTHLY: 'M'
};

export const TRANSMIT_CYCLE_LABELS = {
  [TRANSMIT_CYCLES.DAILY]: '일별',
  [TRANSMIT_CYCLES.WEEKLY]: '주별',
  [TRANSMIT_CYCLES.MONTHLY]: '월별'
};

export const CATEGORY_CODES = {
  P: 'Production',    // 생산
  Q: 'Quality',       // 품질  
  C: 'Cost',         // 비용
  D: 'Delivery',     // 납기
  E: 'Energy',       // 에너지
  S: 'Safety'        // 안전
};

export const CATEGORY_LABELS = {
  P: '생산',
  Q: '품질',
  C: '비용', 
  D: '납기',
  E: '에너지',
  S: '안전'
};

export const TARGET_TYPES = {
  INCREASE: 'INCREASE',  // 높을수록 좋음
  DECREASE: 'DECREASE'   // 낮을수록 좋음
};

// 추세 방향
export const TREND_DIRECTIONS = {
  UP: 'UP',         // 상승
  DOWN: 'DOWN',     // 하락
  STABLE: 'STABLE'  // 보합
};

// KPI 집계 데이터 구조
export const createKPIAggregationData = (data) => ({
  seq: data.SEQ,
  site: data.SITE,
  companyCode: data.COMP_CD,
  aggregationDate: data.AGGREGATION_DATE,
  kpiIndicatorCode: data.KPI_INDICATOR_CD,
  kpiIndicatorName: data.KPI_INDICATOR_NM,
  categoryCode: data.CATEGORY_CD,
  categoryName: data.CATEGORY_NM,
  measuredValue: data.MEASURED_VALUE,
  unit: data.UNIT,
  achievementRate: data.ACHIEVEMENT_RATE,
  currentValue: data.CURRENT_VALUE,
  targetValue: data.TARGET_VALUE,
  targetType: data.TARGET_TYPE,
  dataQualityScore: data.DATA_QUALITY_SCORE,
  createUser: data.CREATE_USER,
  createDate: data.CREATE_DATE,
  updateUser: data.UPDATE_USER,
  updateDate: data.UPDATE_DATE,
  flagActive: data.FLAG_ACTIVE
});

// KPI 원시 데이터 구조
export const createKPIRawData = (data) => ({
  seq: data.SEQ,
  site: data.SITE,
  companyCode: data.COMP_CD,
  dataDate: data.DATA_DATE,
  kpiIndicatorCode: data.KPI_INDICATOR_CD,
  kpiIndicatorName: data.KPI_INDICATOR_NM,
  categoryCode: data.CATEGORY_CD,
  categoryName: data.CATEGORY_NM,
  measuredValue: data.MEASURED_VALUE,
  unit: data.UNIT,
  createUser: data.CREATE_USER,
  createDate: data.CREATE_DATE,
  updateUser: data.UPDATE_USER,
  updateDate: data.UPDATE_DATE,
  flagActive: data.FLAG_ACTIVE
});

// 회사 구독 정보 구조
export const createCompanySubscription = (data) => ({
  seq: data.SEQ,
  site: data.SITE,
  companyCode: data.COMP_CD,
  kpiIndicatorCode: data.KPI_INDICATOR_CD,
  categoryId: data.CATEGORY_ID,
  description: data.DESCRIPTION,
  currentValue: data.CURRENT_VALUE,
  targetValue: data.TARGET_VALUE,
  transmitCycle: data.TRANSMIT_CYCLE,
  sort: data.SORT,
  remark: data.REMARK,
  flagActive: data.FLAG_ACTIVE,
  createUser: data.CREATE_USER,
  createDate: data.CREATE_DATE,
  updateUser: data.UPDATE_USER,
  updateDate: data.UPDATE_DATE
});

// 새로운 백엔드 응답 구조에 맞는 타입 정의
export const createKpiMonitoringDashboardResponse = (data) => ({
  site: data.site,
  siteKpiData: createSiteKpiData(data.siteKpiData),
  compKpiData: data.compKpiData?.map(createCompanyKpiData) || [],
  lastUpdated: data.lastUpdated
});

// 지부별 KPI 데이터 구조
export const createSiteKpiData = (data) => ({
  site: data.site,
  kpiData: data.kpiData?.map(createKpiSiteDetail) || []
});

// KPI 지부별 상세 정보
export const createKpiSiteDetail = (data) => ({
  categoryCode: data.categoryCode,
  categoryName: data.categoryName,
  kpiIndicatorCode: data.kpiIndicatorCode,
  kpiIndicatorName: data.kpiIndicatorName,
  targetValue: data.targetValue,
  currentValue: data.currentValue,
  measuredValue: data.measuredValue,
  achievementRate: data.achievementRate,
  topAchievementRate: data.topAchievementRate,
  bottomAchievementRate: data.bottomAchievementRate,
  unit: data.unit,
  description: data.description,
  participantCompCount: data.participantCompCount
});

// 회사별 KPI 데이터 구조
export const createCompanyKpiData = (data) => ({
  compCd: data.compCd,
  compNm: data.compNm,
  kpiData: data.kpiData?.map(createKpiCompanyDetail) || []
});

// KPI 지표별 상세 정보 (회사용)
export const createKpiCompanyDetail = (data) => ({
  categoryCode: data.categoryCode,
  categoryName: data.categoryName,
  kpiIndicatorCode: data.kpiIndicatorCode,
  kpiIndicatorName: data.kpiIndicatorName,
  targetValue: data.targetValue,
  currentValue: data.currentValue,
  measuredValue: data.measuredValue,
  achievementRate: data.achievementRate,
  unit: data.unit,
  transmitCycle: data.transmitCycle,
  aggregationDate: data.aggregationDate,
  description: data.description,
  previousPeriodValue: data.previousPeriodValue,
  previousPeriodAchievementRate: data.previousPeriodAchievementRate,
  periodComparison: data.periodComparison,
  trendDirection: data.trendDirection
});

// 대시보드용 회사 KPI 요약 데이터 구조 (구 버전 호환)
export const createCompanyKPISummary = (companyData, subscriptions, kpiData) => ({
  companyCode: companyData.COMP_CD,
  companyName: companyData.companyName || companyData.COMP_CD,
  site: companyData.SITE,
  subscriptions: subscriptions.map(sub => ({
    ...createCompanySubscription(sub),
    kpiData: kpiData.filter(kpi => 
      kpi.COMP_CD === sub.COMP_CD && 
      kpi.KPI_INDICATOR_CD === sub.KPI_INDICATOR_CD
    ).map(createKPIAggregationData),
    transmitCycleLabel: TRANSMIT_CYCLE_LABELS[sub.TRANSMIT_CYCLE] || sub.TRANSMIT_CYCLE
  })),
  lastUpdated: new Date().toISOString()
});

// 지부 통계 데이터 구조 (새 구조용)
export const createBranchStatisticsFromSiteKpiData = (siteKpiData) => {
  if (!siteKpiData) {
    return {
      site: 'UNKNOWN',
      siteNm: 'Unknown Site',
      indicators: [],
      lastUpdated: new Date().toISOString()
    };
  }
  
  return {
    site: siteKpiData.site,
    siteNm: siteKpiData.siteNm,
    indicators: siteKpiData.kpiData?.map(kpiDetail => ({
      kpiIndicatorCode: kpiDetail.kpiIndicatorCode,
      kpiIndicatorName: kpiDetail.kpiIndicatorName,
      categoryCode: kpiDetail.categoryCode,
      categoryName: kpiDetail.categoryName,
      unit: kpiDetail.unit,
      measuredValue: kpiDetail.measuredValue || 0,
      averageAchievement: kpiDetail.achievementRate || 0,
      minAchievement: kpiDetail.bottomAchievementRate || 0,
      maxAchievement: kpiDetail.topAchievementRate || 0,
      totalCompanies: kpiDetail.participantCompCount || 0
    })) || [],
    lastUpdated: new Date().toISOString()
  };
};

// 지부 통계 데이터 구조 (구 버전 호환)
export const createBranchStatistics = (site, kpiData) => {
  const groupedByIndicator = {};
  
  kpiData.forEach(kpi => {
    const key = kpi.KPI_INDICATOR_CD;
    if (!groupedByIndicator[key]) {
      groupedByIndicator[key] = {
        kpiIndicatorCode: key,
        kpiIndicatorName: kpi.KPI_INDICATOR_NM,
        categoryCode: kpi.CATEGORY_CD,
        categoryName: kpi.CATEGORY_NM,
        unit: kpi.UNIT,
        companies: [],
        averageAchievement: 0,
        minAchievement: 100,
        maxAchievement: 0,
        totalCompanies: 0
      };
    }
    
    groupedByIndicator[key].companies.push({
      companyCode: kpi.COMP_CD,
      achievementRate: kpi.ACHIEVEMENT_RATE || 0,
      measuredValue: kpi.MEASURED_VALUE,
      targetValue: kpi.TARGET_VALUE
    });
  });
  
  // 통계 계산
  Object.keys(groupedByIndicator).forEach(key => {
    const indicator = groupedByIndicator[key];
    const achievements = indicator.companies.map(c => c.achievementRate);
    
    indicator.averageAchievement = achievements.reduce((sum, rate) => sum + rate, 0) / achievements.length;
    indicator.minAchievement = Math.min(...achievements);
    indicator.maxAchievement = Math.max(...achievements);
    indicator.totalCompanies = achievements.length;
  });
  
  return {
    site,
    indicators: Object.values(groupedByIndicator),
    lastUpdated: new Date().toISOString()
  };
};